// One task row, used on Tasks, Overview (needs attention) and ProjectDetail.
// Inline editing per the ledger: click title → input, click due → date
// picker, click project tag → searchable picker. Checkbox completes (or
// reopens) with optimistic flash; every change PATCHes one field.

import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import type { ProjectPhase } from '../../shared/types';
import { PHASE_DOTS } from '../lib/phaseDots';
import { CalendarPanel, Dropdown, formatDayMonth, Modal, type DropdownItem } from './primitives';
import { CalendarIcon, CheckIcon, FlagIcon, MNoteIcon, MoreIcon, MoveIcon, NoteIcon, TrashIcon } from './icons';
import { TaskPanel } from './TaskPanel';
import { TaskSheet, priorityMenuItems } from './TaskSheet';
import { DateSheet } from './DateSheet';
import { PageMenu } from './PageMenu';
import { PickerSheet } from './PickerSheet';
import { SwipeRow } from './SwipeRow';
import { useIsMobile } from '../lib/useIsMobile';
import { checkProgress } from '../../shared/taskNotes';

export interface TaskRowData {
  id: string;
  title: string;
  /** Plain-text notes body (§7) — drives the row indicator and the panel. */
  notes?: string | null;
  status: string;
  priority: number;
  due_date: string | null;
  project_id: string | null;
  project_name?: string | null;
  /** Set when a recurring-task definition generated this row (feature-builds,
      July 2026) — drives the small ↻ marker after the title. */
  recurrence_id?: string | null;
}

export interface ProjectOption {
  id: string;
  name: string;
  phase: ProjectPhase;
  sub?: string;
  group?: string;
}

export { PHASE_DOTS };

/** Quiet per-row trash (Phase 1.11) — same two-step arm/confirm contract as
    Crew's DeleteCrewButton (pages/Crew.tsx): quiet "×" until hover, click
    arms for 4s, a second click within the window confirms. */
function DeleteTaskButton({ onConfirm }: { onConfirm: () => void }) {
  const [arming, setArming] = useState(false);
  useEffect(() => {
    if (!arming) return;
    const t = setTimeout(() => setArming(false), 4000);
    return () => clearTimeout(t);
  }, [arming]);
  return (
    <span
      className="row-x"
      title={arming ? 'Click again to remove' : 'Move to trash'}
      onClick={(e) => {
        e.stopPropagation();
        if (arming) onConfirm();
        else setArming(true);
      }}
    >
      {arming ? '✕' : '×'}
    </span>
  );
}

export function TaskRow({
  task,
  today,
  projects,
  projectDisplay = 'tag',
  notesMode = 'modal',
  selected = false,
  selectable,
  onOpenNotes,
  onChanged,
  onRemoved,
}: {
  task: TaskRowData;
  today: string;
  /** Pass the project list to enable the inline project picker. */
  projects?: ProjectOption[];
  /** 'tag' = mono chip (04-tasks), 'context' = plain text (01-overview), 'none' = hidden. */
  projectDisplay?: 'tag' | 'context' | 'none';
  /** 'modal' = the row opens the notes panel in a Modal itself (Overview,
      ProjectDetail); 'panel' = the page owns a side panel and just gets
      told which task to show (Tasks). */
  notesMode?: 'panel' | 'modal';
  /** Panel mode only — highlights the row whose notes are open. */
  selected?: boolean;
  selectable?: { selected: boolean; onToggle: () => void };
  onOpenNotes?: () => void;
  onChanged: () => void;
  /** Overview only — the row left the attention list (completed or deleted),
      so the page can drop it now and ignore any refresh that still has it. */
  onRemoved?: (id: string) => void;
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [draft, setDraft] = useState(task.title);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Optimistic done-state override: covers the gap between the PATCH
  // resolving and the parent's refresh landing (the bare busy flag used to
  // let the circle flicker empty there). Cleared when the prop catches up.
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  // True only when THIS interaction completed the task — gates the check
  // animation so already-done rows don't all pop on mount/tab switch.
  const [justDone, setJustDone] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const dueRef = useRef<HTMLSpanElement>(null);
  // Mobile only (§0.6): the task sheet, and which sheet is stacked over the
  // row from a left swipe or the row's "More" menu.
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [rowMenu, setRowMenu] = useState<null | 'more' | 'project' | 'priority' | 'trash' | 'date'>(null);

  const done = optimistic ?? task.status === 'done';
  useEffect(() => setOptimistic(null), [task.status]);
  const overdue = !done && task.due_date !== null && task.due_date < today;

  const patch = async (fields: Record<string, unknown>): Promise<boolean> => {
    const ok = await api(`/api/admin/tasks/${task.id}`, { method: 'PATCH', json: fields })
      .then(() => true)
      .catch((error) => {
        reportError(error, 'Could not update the task.');
        return false;
      });
    onChanged();
    return ok;
  };

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    const next = !done;
    setOptimistic(next);
    setJustDone(next);
    const ok = await patch({ status: next ? 'done' : 'todo' });
    if (ok && next) onRemoved?.(task.id);
    if (!ok) {
      setOptimistic(null);
      setJustDone(false);
    }
    setBusy(false);
  };

  const commitTitle = () => {
    setEditingTitle(false);
    const t = draft.trim();
    if (t && t !== task.title) patch({ title: t });
  };

  const dueMeta = (): { text: string; cls: string } => {
    if (!task.due_date) return { text: '—', cls: ' dim' };
    if (done) return { text: formatDayMonth(task.due_date), cls: ' dim' };
    if (task.due_date < today) return { text: formatDayMonth(task.due_date), cls: ' danger' };
    if (task.due_date === today) return { text: 'Today', cls: ' gold' };
    return { text: formatDayMonth(task.due_date), cls: ' dim' };
  };
  const meta = dueMeta();
  const progress = checkProgress(task.notes);
  const hasNotes = (task.notes ?? '').trim() !== '';

  const openNotes = () => {
    if (notesMode === 'panel') onOpenNotes?.();
    else setNotesOpen(true);
  };

  const projectItems: DropdownItem<string>[] = [
    ...(projects ?? []).map((p) => ({ value: p.id, label: p.name, sub: p.sub, group: p.group, dotColor: PHASE_DOTS[p.phase] })),
    { value: '', label: 'No project', dotColor: 'transparent' },
  ];

  // Mobile trash — same DELETE the desktop DeleteTaskButton confirms into,
  // reached from the row's "More" sheet instead of the two-step ×.
  const removeTask = () =>
    api(`/api/admin/tasks/${task.id}`, { method: 'DELETE' })
      .then(() => {
        onRemoved?.(task.id);
      })
      .catch((error) => reportError(error, 'Could not delete the task.'))
      .finally(() => onChanged());

  if (isMobile) {
    // The meta line's tone comes off the same dueMeta() the desktop uses:
    // red overdue, gold today, dim otherwise (mockup §3.1 A `.sub .d/.g`).
    const dueTone = meta.cls === ' danger' ? 'd' : meta.cls === ' gold' ? 'g' : '';
    return (
      <>
        <SwipeRow
          disabled={!!selectable}
          rightAction={{
            label: done ? 'Reopen' : 'Done',
            icon: <CheckIcon />,
            tone: done ? 'grey' : 'ok',
            onSelect: () => void toggle(),
          }}
          leftActions={[
            { label: 'Date', icon: <CalendarIcon />, tone: 'gold', onSelect: () => setRowMenu('date') },
            { label: 'More', icon: <MoreIcon />, tone: 'grey', onSelect: () => setRowMenu('more') },
          ]}
        >
          <div
            className={`row mrow-task${task.priority === 1 && !done ? ' flag' : ''}${(selectable ? selectable.selected : selected) ? ' sel' : ''}`}
            onClick={() => selectable ? selectable.onToggle() : setSheetOpen(true)}
          >
            {selectable ? <button type="button" className="task-select-hit" role="checkbox"
              aria-label={`Select ${task.title}`} aria-checked={selectable.selected}
              onClick={e => { e.stopPropagation(); selectable.onToggle(); }}>
              <span className={`check task-select-check${selectable.selected ? ' done' : ''}`} />
            </button> : <span
              className={`check${overdue ? ' overdue' : ''}${done ? ' done' : ''}${justDone ? ' just' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                void toggle();
              }}
            />}
            <div className="main">
              <div className={`ttl${done ? ' dim' : ''}`}>{task.title}</div>
              <div className="sub">
                {projectDisplay !== 'none' && (
                  <>
                    {/* The project is the part that ellipsizes; the date
                        always shows (Phase A — a long project name used to
                        push "12 Jun" off the line). */}
                    <span className="proj">{task.project_name ?? 'No project'}</span>
                    <span className="sep">·</span>
                  </>
                )}
                <span className={dueTone || undefined}>{meta.text}</span>
                {task.recurrence_id && (
                  <>
                    <span className="sep">·</span>↻
                  </>
                )}
              </div>
            </div>
            {progress.total > 0 ? (
              <span className="tail n">
                {progress.done}/{progress.total}
              </span>
            ) : hasNotes ? (
              <span className="tail">
                <MNoteIcon />
              </span>
            ) : null}
          </div>
        </SwipeRow>

        {sheetOpen && (
          <TaskSheet
            task={task}
            today={today}
            projects={projects}
            onChanged={onChanged}
            onRemoved={onRemoved}
            onClose={() => setSheetOpen(false)}
          />
        )}

        {/* Every swipe action is also in here — nothing is gesture-only. */}
        <PageMenu
          open={rowMenu === 'more'}
          onClose={() => setRowMenu(null)}
          items={[
            { key: 'date', icon: <CalendarIcon />, label: 'Due date', onSelect: () => setRowMenu('date') },
            ...(projects
              ? [
                  {
                    key: 'project',
                    icon: <MoveIcon />,
                    label: 'Change project',
                    onSelect: () => setRowMenu('project'),
                  },
                ]
              : []),
            { key: 'priority', icon: <FlagIcon />, label: 'Priority', onSelect: () => setRowMenu('priority') },
            {
              key: 'trash',
              icon: <TrashIcon />,
              label: 'Move to trash',
              tone: 'danger' as const,
              onSelect: () => setRowMenu('trash'),
            },
          ]}
        />

        <PickerSheet
          open={rowMenu === 'project'}
          title="Project"
          items={projectItems}
          value={task.project_id ?? ''}
          searchable
          onSelect={(v) => {
            setRowMenu(null);
            void patch({ project_id: v === '' ? null : v });
          }}
          onClose={() => setRowMenu(null)}
        />

        <PageMenu
          open={rowMenu === 'priority'}
          onClose={() => setRowMenu(null)}
          title="Priority"
          items={priorityMenuItems(task.priority, (priority) => {
            setRowMenu(null);
            void patch({ priority });
          })}
        />

        <DateSheet
          open={rowMenu === 'date'}
          title="Due date"
          value={task.due_date}
          allowClear
          onPick={(iso) => {
            setRowMenu(null);
            void patch({ due_date: iso });
          }}
          onClose={() => setRowMenu(null)}
        />

        <PageMenu
          open={rowMenu === 'trash'}
          onClose={() => setRowMenu(null)}
          title="Move to trash?"
          items={[
            {
              key: 'confirm',
              icon: <TrashIcon />,
              label: 'Move to trash',
              tone: 'danger' as const,
              onSelect: () => {
                setRowMenu(null);
                void removeTask();
              },
            },
            { key: 'cancel', label: 'Cancel', onSelect: () => setRowMenu(null) },
          ]}
        />
      </>
    );
  }

  return (
    <div className={`row${pickerOpen ? ' menu-open' : ''}${pickerOpen || dateOpen || editingTitle ? ' editing' : ''}${selected ? ' sel' : ''}`}>
      <span
        className={`check${overdue ? ' overdue' : ''}${done ? ' done' : ''}${justDone ? ' just' : ''}`}
        onClick={toggle}
      />
      {editingTitle ? (
        <input
          className="inline-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitTitle();
            if (e.key === 'Escape') setEditingTitle(false);
          }}
          onBlur={commitTitle}
          autoFocus
        />
      ) : (
        <span
          className="row-title editable"
          title={task.title}
          tabIndex={0}
          style={done ? { textDecoration: 'line-through', color: 'var(--text-faint)' } : undefined}
          onClick={() => {
            setDraft(task.title);
            setEditingTitle(true);
          }}
          // Enter on a focused (not yet editing) title opens the notes —
          // clicking is what starts an inline rename.
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              openNotes();
            }
          }}
        >
          {task.title}
        </span>
      )}
      {task.recurrence_id && <span className="rec-tag" title="Generated by a recurring task">↻</span>}
      {task.priority === 1 && !done && <span className="priority-flag">⚑</span>}
      {projectDisplay === 'context' && task.project_name && (
        <span className="row-context" title={task.project_name}>{task.project_name}</span>
      )}
      <span className="row-spacer" />
      {projectDisplay === 'tag' && (
        <span className="project-select">
          <span
            className="project-tag"
            title={task.project_name ?? 'No project'}
            style={{ cursor: projects ? 'pointer' : undefined }}
            onClick={() => projects && setPickerOpen((o) => !o)}
          >
            {task.project_name ?? 'No project'}
            {projects && <span className="caret">▾</span>}
          </span>
          {pickerOpen && (
            <Dropdown
              items={projectItems}
              value={task.project_id ?? ''}
              onSelect={(v) => patch({ project_id: v === '' ? null : v })}
              onClose={() => setPickerOpen(false)}
              searchable
            />
          )}
        </span>
      )}
      <span
        className={`notes-ind${hasNotes ? ' has' : ''}`}
        title={hasNotes ? 'Notes' : 'Add notes'}
        onClick={openNotes}
      >
        <NoteIcon className="notes-ico" />
        {progress.total > 0 && (
          <span className="n">
            {progress.done}/{progress.total}
          </span>
        )}
      </span>
      <span
        ref={dueRef}
        className={`row-meta col-due editable${meta.cls}`}
        onClick={() => setDateOpen((o) => !o)}
      >
        {meta.text}
      </span>
      {dateOpen && (
        <CalendarPanel
          value={task.due_date}
          allowClear
          anchorRef={dueRef}
          onPick={(iso) => patch({ due_date: iso })}
          onClose={() => setDateOpen(false)}
        />
      )}
      <DeleteTaskButton
        onConfirm={() =>
          api(`/api/admin/tasks/${task.id}`, { method: 'DELETE' })
            .then(() => {
              onRemoved?.(task.id);
            })
            .catch((error) => reportError(error, 'Could not delete the task.'))
            .finally(() => onChanged())
        }
      />
      {notesOpen && (
        <Modal title="Task notes" context="TASKS" width={520} onClose={() => setNotesOpen(false)}>
          <TaskPanel task={task} today={today} onChanged={onChanged} onClose={() => setNotesOpen(false)} />
        </Modal>
      )}
    </div>
  );
}

/** "+ Add task…" row — POSTs with the given defaults, stays open for rapid entry. */
export function QuickAddRow({
  hint,
  defaults,
  onAdded,
  openSignal,
}: {
  hint: string;
  defaults?: { due_date?: string | null; project_id?: string | null };
  onAdded: () => void;
  /** Bump this counter to open + focus the row from outside (header + Task). */
  openSignal?: number;
}) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (openSignal !== undefined && openSignal > 0) {
      setAdding(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [openSignal]);

  const submit = async () => {
    const t = title.trim();
    if (!t) return;
    try {
      await api('/api/admin/tasks', { method: 'POST', json: { title: t, ...defaults } });
    } catch (error) {
      reportError(error, 'Could not create the task.');
      return;
    }
    setTitle('');
    onAdded();
    inputRef.current?.focus();
  };

  return (
    <div className="quick-add" onClick={() => setAdding(true)}>
      <span className="plus">+</span>
      {adding ? (
        <input
          ref={inputRef}
          value={title}
          placeholder={hint}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') {
              setAdding(false);
              setTitle('');
            }
          }}
          onBlur={() => {
            if (!title.trim()) setAdding(false);
          }}
          autoFocus
        />
      ) : (
        <span className="hint">{hint}</span>
      )}
    </div>
  );
}
